import EmberObject, { computed, set } from '@ember/object';
import { copy as EmberCopy } from 'ember-copy';

import utilities from '../utils/utilities';
import formulas from '../utils/formulas';
import validation from '../utils/validation';
import intersection from '../utils/intersection';

import camelCase from 'lodash.camelcase';
import cloneDeep from 'lodash.clonedeep';

const GAIN_STAGES = ['', 'input', 'output'];

var gainData2Objects = function (pgd) {
  var parsedData = {};
  for (var i in pgd) {
    parsedData[i] = cloneDeep(pgd[i]);
    parsedData[i].id = i;
  }
  return parsedData;
};

var findSelectedInputByValue = function (gainInputs, value) {
  let foundInput = gainInputs.find((input) => input.id == value);
  return foundInput ? EmberCopy(foundInput, true) : gainInputs.length ? EmberCopy(gainInputs[0], true) : null;
};

var getGainInput = function (gainValue) {
  return {
    id: gainValue,
    description: Number(gainValue.toString().substring(0, 6)),
  };
};

var convertGainDataToInput = function (gainData, defaultGainSetting) {
  let gainInputs = [],
    gainType = null;

  if (Array.isArray(gainData)) {
    if (gainData.length === 1) {
      gainType = 'fixed';
      gainInputs.push(getGainInput(gainData[0]));
    } else {
      gainType = 'discrete';
      //var selected = 0;
      for (var g = 0; g < gainData.length; g++) {
        gainInputs.push(getGainInput(gainData[g]));
      }
    }
  } else {
    gainInputs.push(getGainInput(defaultGainSetting));
    gainType = 'range';
  }

  return {
    inputs: gainInputs,
    type: gainType,
    selected: findSelectedInputByValue(gainInputs, defaultGainSetting),
  };
};

var findCurrentGainStages = function (part) {
  return GAIN_STAGES.filter((gainStage) => camelCase(`${gainStage}GainData`) in part);
};

var handlePartGainData = function (part, toolConfigData) {
  let defaultItem = toolConfigData.partInputDefaultsOrganized[part.id],
    currentStages = findCurrentGainStages(part);

  currentStages.forEach((gainStage) => {
    let gainSettingKey = camelCase(`${gainStage}GainSetting`),
      gainDataKey = camelCase(`${gainStage}GainData`),
      gainInputsKey = camelCase(`${gainStage}GainInputs`),
      gainTypeKey = camelCase(`${gainStage}GainType`),
      gainSelectedInputKey = camelCase(`${gainStage}SelectedGainInput`);

    let defaultGainSetting = defaultItem && gainSettingKey in defaultItem ? defaultItem[gainSettingKey] : null,
      gainData = part[gainDataKey];

    let result = convertGainDataToInput(gainData, defaultGainSetting);

    part[gainInputsKey] = result.inputs;
    part[gainTypeKey] = result.type;
    part[gainSelectedInputKey] = result.selected;
  });

  if (currentStages.includes('input') && currentStages.includes('output')) {
    part.gainType = part.inputGainType;
    let result = refreshGain(part);
    if (result) {
      part.selectedGainInput = result.selectedGainInput;
      part.gainInputs = result.gainInputs;
    }
  }
};

var refreshGain = function (part) {
  if ('inputSelectedGainInput' in part && 'outputSelectedGainInput' in part) {
    let gainValue = part.inputSelectedGainInput.id * part.outputSelectedGainInput.id;
    return {
      selectedGainInput: getGainInput(gainValue),
      gainInputs: [getGainInput(gainValue)],
    };
  }
};

var turnPartDataToObjects = function (toolConfigData) {
  var dataUnparsed = toolConfigData.partDataOrganized,
    parsedData = [];

  for (var item in dataUnparsed) {
    var part = cloneDeep(dataUnparsed[item]);
    part.id = item;

    handlePartGainData(part, toolConfigData);

    part.display = `${item}: ${part.description}`;
    part.error = '';
    part.isSelected = false;
    part.url = toolConfigData.productPageURL[item];

    parsedData.push(part);
  }
  parsedData = parsedData.sort(function (a, b) {
    return a.order - b.order;
  });

  return parsedData;
};

var getPart = function (parts, defaultPart) {
  var result,
    len = parts.length;

  for (var i = 0; i < len; i++) {
    if (parts[i].id.toLowerCase() == defaultPart.toLowerCase()) {
      result = parts[i];
      result.isSelected = true;
      break;
    }
  }
  return result;
};

var createLUT = function (src) {
  var lut = [];
  for (var i = 0; i < src.length; i++) {
    lut[src[i].id] = src[i];
  }
  return lut;
};

var isValidNumber = function (obj) {
  return !Array.isArray(obj) && obj - parseFloat(obj) + 1 >= 0;
};

var setSliderToMin = function (l, r, sliderString, context) {
  var min = Math.min(l, r),
    max = Math.max(l, r),
    slider = context.get(sliderString);

  if (slider < min || slider > max) context.set(sliderString, min);
};

var setSlider = function (l, r, sliderString, val, context) {
  var min = Math.min(l, r),
    max = Math.max(l, r);

  if (val >= min && val <= max) context.set(sliderString, val);
  else context.set(sliderString, min);
};

var compare = function (a, b) {
  return a.order < b.order ? -1 : a.order > b.order ? 1 : 0;
};

var calculateDefaultAxis = function (vsRange, minCalc, maxCalc) {
  var numberTicks = null,
    min = null,
    max = null;

  if (vsRange > 10) {
    min = Math.floor((minCalc * 1) / 5) * 5 - 5;
    max = Math.ceil((maxCalc * 1) / 5) * 5 + 5;
    numberTicks = (max - min) / 5 + 1;
  } else {
    min = Math.floor(minCalc) - 1;
    max = Math.ceil(maxCalc) + 1;
    numberTicks = max - min + 1;
  }

  return {
    min: min,
    max: max,
    numberTicks: numberTicks,
  };
};

////////////////////////////////////////////////////////////////////////////////////
// data.js: Model structure of Diamond,                                           //
// I wonder if we should have renamed this as index???                            //
////////////////////////////////////////////////////////////////////////////////////

export default class Data extends EmberObject {
  // Url
  urlString = '';
  urlQuestion = '';
  urlSpecifiesDevice = false;

  // Parts
  parts = null;
  selectedPart = null;
  recommendedParts = null;
  numOfRecommendedParts = null;

  // Inputs and Validation
  projectData = null;
  selectedTab = null;
  validationResults = null;

  // Input Signals
  inputSignalTypes = null;
  currentSignalType = null;

  // View Size
  viewSize = null;
  viewSizePx = null;

  // Graph Data
  gainDep = null;
  polyColors = null;
  plotColors = null;
  plotStyle = null;
  currentPolygons = null;
  innerPolygon = null; // this can not be contained in currentPolygons,  because the intersection algorithm would treat it as another polygon
  innerPolygonError = false;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  init() {
    super.init(...arguments);

    this.innerPolygon = [];
    this.currentPolygons = {
      inputSignal: [],
      preAmplifierInput: [],
      preAmplifierOutput3Amp: [],
      subtractorInputOutput: [],
      outputAmplifier2Amp: [],
      feedback1: [],
      feedback2: [],
      outputPinBoundry: [],
      debug: [],
    };
    this.validationResults = {
      isAllValid: true,
      isGain: true,
      isVref: true,
      isVs: true,
      isIntrnCircuit: true,
      isOperatingRange: 1,
    };
    this.inputSignalTypes = [
      { description: this.intl.t('typeDifferentialDescription'), id: 1, type: this.intl.t('typeDifferential') },
      { description: this.intl.t('typeSingleEndedPosDescription'), id: 2, type: this.intl.t('typeSingleEndedPos') },
      { description: this.intl.t('typeSingleEndedNegDescription'), id: 3, type: this.intl.t('typeSingleEndedNeg') },
    ];
    this.selectedTab = this.toolConfigData.selectedTab;
    set(this, 'currentSignalType', this.inputSignalTypes[0]);

    this.parts = turnPartDataToObjects(this.toolConfigData);

    this.polyColors = [];
    this.plotStyle = [];

    var gPartId = this.globalPartId ? this.globalPartId : false,
      gPartIdValid = false;
    if (document.referrer && !gPartId) {
      try {
        var referrer = document.referrer.toLowerCase();
        for (var i = 0; i < this.parts.length; i++) {
          if (referrer.indexOf(this.parts[i].id.toLowerCase()) !== -1) {
            gPartId = this.parts[i].id;
            break;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (gPartId) {
      for (let i = 0; i < this.parts.length; i++) {
        if (this.parts[i].id.toLowerCase() == gPartId.toLowerCase()) {
          var lut = createLUT(this.parts);
          this.set('selectedPart', lut[this.parts[i].id]);
          gPartIdValid = true;
          break;
        }
      }
    }

    this.set(
      'selectedPart',
      gPartIdValid ? getPart(this.parts, gPartId) : getPart(this.parts, this.toolConfigData.defaultPart),
    );

    this.gainDep = gainData2Objects(this.toolConfigData.partGainDepDataOrganized);

    this.setInputs();

    this.plotColors = this.toolConfigData.plotColors;
    set(this, 'recommendedParts', []);
    this.viewSize = '3';
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Computed Values                                                            //
  ////////////////////////////////////////////////////////////////////////////////

  @computed('adiBaseURL', 'selectedPart.id', 'toolConfigData.productPageURL') get selectedPartUrl() {
    return this.adiBaseURL + '/' + this.toolConfigData.productPageURL[this.selectedPart.id];
  }

  @computed('projectData.{vDiffLeft,vDiffRight}') get vDiffs() {
    return [Number(this.projectData.vDiffLeft), Number(this.projectData.vDiffRight)];
  }

  @computed('projectData.{varTypeLeft,varTypeRight}') get varTypes() {
    return [Number(this.projectData.varTypeLeft), Number(this.projectData.varTypeRight)];
  }

  @computed('projectData.{vDiffLeft,vDiffRight}') get vDiffEqual() {
    return this.projectData.vDiffLeft == this.projectData.vDiffRight ? true : false;
  }

  @computed('projectData.{varTypeLeft,varTypeRight}') get varTEqual() {
    return this.projectData.varTypeLeft == this.projectData.varTypeRight ? true : false;
  }

  @computed('isDiffPGIASchematic', 'projectData.vDiffLeft', 'selectedPart.selectedGainInput.id', 'vref')
  get vOutLeft() {
    let result = this.projectData.vDiffLeft * this.selectedPart.selectedGainInput.id;
    if (!this.isDiffPGIASchematic) result += this.vref;
    return result;
  }

  @computed('isDiffPGIASchematic', 'projectData.vDiffRight', 'selectedPart.selectedGainInput.id', 'vref')
  get vOutRight() {
    let result = this.projectData.vDiffRight * this.selectedPart.selectedGainInput.id;
    if (!this.isDiffPGIASchematic) result += this.vref;
    return result;
  }

  @computed('projectData.{vDiffLeft,vDiffRight,varTypeLeft,varTypeRight}', 'currentSignalType.id') get vCms() {
    var dL = this.projectData.vDiffLeft,
      dR = this.projectData.vDiffRight,
      vL = this.projectData.varTypeLeft,
      vR = this.projectData.varTypeRight,
      type = this.currentSignalType.id,
      vcms = [];
    if (dL == dR && vL == vR) {
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dL / 2 : Number(vL) - dL / 2);
    } else if (dL == dR) {
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dL / 2 : Number(vL) - dL / 2);
      vcms.push(type == 1 ? Number(vR) : type == 2 ? Number(vR) + dL / 2 : Number(vR) - dL / 2);
    } else if (vL == vR) {
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dL / 2 : Number(vL) - dL / 2);
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dR / 2 : Number(vL) - dR / 2);
    } else {
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dL / 2 : Number(vL) - dL / 2);
      vcms.push(type == 1 ? Number(vL) : type == 2 ? Number(vL) + dR / 2 : Number(vL) - dR / 2);
      vcms.push(type == 1 ? Number(vR) : type == 2 ? Number(vR) + dL / 2 : Number(vR) - dL / 2);
      vcms.push(type == 1 ? Number(vR) : type == 2 ? Number(vR) + dR / 2 : Number(vR) - dR / 2);
    }
    return vcms;
  }

  @computed(
    'isDiffPGIASchematic',
    'isDiffTIAPGIASchematic',
    'projectData.{vsNeg,vsNegIn,vsNegOut,vsPos,vsPosIn,vsPosOut}',
  )
  get axesDefaults() {
    let xAxis = null,
      yAxis = null;

    if (this.isDiffPGIASchematic) {
      let negOut = this.isDiffTIAPGIASchematic ? this.projectData.vsNegOut : this.projectData.vsNegIn,
        negIn = this.projectData.vsNegIn;

      let range = this.projectData.vsPosOut - negOut;
      xAxis = calculateDefaultAxis(range, -1 * range, range);

      range = this.projectData.vsPosIn - negIn;
      yAxis = calculateDefaultAxis(range, negIn, this.projectData.vsPosIn);
    } else {
      let vsRange = this.projectData.vsPos - this.projectData.vsNeg;

      xAxis = calculateDefaultAxis(vsRange, this.projectData.vsNeg, this.projectData.vsPos);
      yAxis = cloneDeep(xAxis);
    }

    return {
      xmin: xAxis.min,
      xmax: xAxis.max,
      xticks: xAxis.numberTicks,
      ymin: yAxis.min,
      ymax: yAxis.max,
      yticks: yAxis.numberTicks,
    };
  }

  @computed('selectedPart.{rNumerator,selectedGainInput.id,gda}') get rg() {
    var result = 'open';
    // NOTE: This still may be used
    //if (this.selectedPart.diagramType.toUpperCase() === 'PINSTRAPPED') {
    //    var gains = this.selectedPart.gainInputs,
    //        gain = this.selectedPart.selectedGainInput;
    //    var smaller = Math.min(gains[0].id, gains[1].id),
    //            larger = Math.max(gains[0].id, gains[1].id);
    //    switch (gain.id) {
    //        case smaller:
    //            result = "open";
    //            break;
    //        case larger:
    //            result = (this.selectedPart.rNumerator / (this.selectedPart.selectedGainInput.id - this.selectedPart.gda)).toFixed(2);
    //            break;
    //        default:
    //            result = "open";
    //    }
    //} else {
    result = (this.selectedPart.rNumerator / (this.selectedPart.selectedGainInput.id - this.selectedPart.gda)).toFixed(
      2,
    );
    if (result == 'Infinity' || result == '-Infinity') result = 'open';
    //}
    return result;
  }

  @computed('rg') get rgInf() {
    return this.rg.toLowerCase() === 'open' ? true : false;
  }

  @computed('projectData.{vref,vref2,vref1}') get vref() {
    if (this.projectData.vref || this.projectData.vref === 0) return this.projectData.vref;
    else if (
      (this.projectData.vref1 || this.projectData.vref1 === 0) &&
      (this.projectData.vref2 || this.projectData.vref2 === 0)
    )
      return (this.projectData.vref1 + this.projectData.vref2) / 2;
    else return null;
  }

  @computed('recommendedParts.length', 'toolConfigData.pstURL', 'urlQuestion') get recommendedInAmpsUrl() {
    var urlString = this.toolConfigData.pstURL + this.urlQuestion;
    for (var t = 0; t < this.recommendedParts.length; t++) {
      if (t === 0) urlString += '#/p0=' + this.recommendedParts[t].id;
      else urlString += '|' + this.recommendedParts[t].id;
    }
    return urlString;
  }

  @computed('numOfRecommendedParts') get hasRecommendedParts() {
    return this.numOfRecommendedParts > 0;
  }

  @computed('selectedPart.id') get currentGainStages() {
    return findCurrentGainStages(this.selectedPart);
  }

  @computed('selectedPart.inAmpType') get isDiffPGIASchematic() {
    return this.isDiffPGIAPart(this.selectedPart);
  }

  @computed('selectedPart.inAmpType') get isDiffTIAPGIASchematic() {
    return this.isDiffTIAPGIAPart(this.selectedPart);
  }

  @computed('selectedPart.inAmpType') get isDiff3AmpPGIASchematic() {
    return this.isDiff3AmpPGIAPart(this.selectedPart);
  }

  @computed('selectedPart.inAmpType') get isThreeAmpClampedSchematic() {
    return this.isThreeAmpClampedPart(this.selectedPart);
  }

  @computed('selectedPart.inAmpType') get isThreeAmpOrClampedSchematic() {
    return this.isThreeAmpOrClampedPart(this.selectedPart);
  }

  @computed('showFirstNegSupply', 'isDiff3AmpPGIASchematic', 'isThreeAmpClampedSchematic', 'isDiffTIAPGIASchematic')
  get selectedSchematic() {
    if (!this.showFirstNegSupply) {
      return 5;
    } else if (this.isThreeAmpClampedSchematic) {
      return 4;
    } else if (this.isDiff3AmpPGIASchematic) {
      return 3;
    } else if (this.isDiffTIAPGIASchematic) {
      return 2;
    } else {
      return 1;
    }
  }

  @computed('selectedPart.diagramType') get showFirstNegSupply() {
    return this.selectedPart.diagramType.toUpperCase() != 'ADA4255';
  }

  @computed('selectedPart.id') get usesChargePumpSupply() {
    return this.partUsesChargePumpSupply(this.selectedPart);
  }

  @computed(
    'isDiff3AmpPGIASchematic',
    'isDiffTIAPGIASchematic',
    'isThreeAmpClampedSchematic',
    'selectedPart.{diagramType,gainType,inAmpType}',
  )
  get selectedDiagram() {
    let s = this.selectedPart,
      d = null;

    if (this.isDiffTIAPGIASchematic) {
      d = 7;
    } else if (this.isDiff3AmpPGIASchematic) {
      d = 8;
    } else if (this.isThreeAmpClampedSchematic) {
      d = 1;
    } else if (s.diagramType.toUpperCase() === 'PINSTRAPPED' && s.inAmpType.toLowerCase() === '3amp') {
      d = 5;
    } else if (s.inAmpType.toLowerCase() === '2amp') {
      d = 3;
    } else if (s.gainType.toLowerCase() === 'fixed') {
      d = 6;
    } else if (s.gainType.toLowerCase() === 'range' && s.inAmpType.toLowerCase() === '3amp') {
      d = 1;
    } else if (
      (s.gainType.toLowerCase() === 'discrete' && s.diagramType.toUpperCase() === 'DIGPROGRAM') ||
      s.diagramType.toUpperCase() === 'PINPROGRAM'
    ) {
      d = 2;
    } else if (s.inAmpType.toLowerCase() === 'current') {
      d = 4;
    }

    return d;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Model Data Building Functions                                              //
  ////////////////////////////////////////////////////////////////////////////////

  refreshGainForSelectedPart() {
    let result = refreshGain(this.selectedPart);
    if (result) {
      this.set('selectedPart.selectedGainInput', result.selectedGainInput);
      this.set('selectedPart.gainInputs', result.gainInputs);

      /**
       * Ensure that the gain inputs in the corresponding selected part in the list of (all) parts
       * is updated for validation
       **/
      let part = this.parts.find((part) => part.id === this.selectedPart.id);
      set(part, 'selectedGainInput', result.selectedGainInput);
      set(part, 'gainInputs', result.gainInputs);
    }
  }

  updateCalculatedProjectData() {
    if (this.usesChargePumpSupply) {
      let p = this.selectedPart,
        supplyCalc = (m, b) => m * this.projectData.chargePumpSupply + b;

      this.set('projectData.vsPosIn', supplyCalc(p.inVspM, p.inVspB));
      this.set('projectData.vsNegIn', supplyCalc(p.inVsmM, p.inVsmB));
    }
  }

  isThreeAmpOrClampedPart(part) {
    return ['3ampclamped', '3amp'].includes(part.inAmpType.toLowerCase());
  }

  isThreeAmpClampedPart(part) {
    return part.inAmpType.toLowerCase() === '3ampclamped';
  }

  isDiffPGIAPart(part) {
    return this.isDiffTIAPGIAPart(part) || this.isDiff3AmpPGIAPart(part);
  }

  isDiffTIAPGIAPart(part) {
    return ['diffpgia', 'difftia-pgia'].includes(part.inAmpType.toLowerCase());
  }

  isDiff3AmpPGIAPart(part) {
    return part.inAmpType.toLowerCase() === 'diff3amppgia';
  }

  partUsesChargePumpSupply(selectedPart) {
    let defaults = this.toolConfigData.partInputDefaultsOrganized,
      defaultItem = selectedPart ? defaults[selectedPart.id] : null;
    return defaultItem && 'chargePumpSupply' in defaultItem;
  }

  // Set Project Data and Gain
  setInputs() {
    let defaults = this.toolConfigData.partInputDefaultsOrganized,
      defaultItem = defaults[this.selectedPart.id],
      projectData = {
        varTypeLeft: defaultItem.varTypeLeft,
        varTypeRight: defaultItem.varTypeRight,
        vDiffLeft: defaultItem.vDiffLeft,
        vDiffRight: defaultItem.vDiffRight,
        varTypeSlider: defaultItem.varTypeLeft,
        vDiffSlider: defaultItem.vDiffLeft,
      };

    if (this.usesChargePumpSupply) projectData.chargePumpSupply = defaultItem.chargePumpSupply;

    if (this.isDiffPGIASchematic) {
      projectData.vref = defaultItem.vocm;

      projectData.vsPosIn = defaultItem.vsPosIn;
      projectData.vsPosOut = defaultItem.vsPosOut;

      projectData.vsNegIn = defaultItem.vsNegIn;
      projectData.vsNegOut = defaultItem.vsNegOut;
    } else if (this.isThreeAmpClampedSchematic) {
      projectData.vref2 = defaultItem.vref2;
      projectData.vref1 = defaultItem.vref1;

      projectData.vsPos = defaultItem.vsPos;
      projectData.vsNeg = defaultItem.vsNeg;

      projectData.vclhi = defaultItem.vclhi;
      projectData.vcllo = defaultItem.vcllo;
    } else {
      projectData.vref = defaultItem.vref;
      projectData.vsPos = defaultItem.vsPos;
      projectData.vsNeg = defaultItem.vsNeg;
    }

    this.set('projectData', projectData);
    this.updateCalculatedProjectData();

    this.set('currentSignalType', EmberCopy(this.inputSignalTypes[0], true));

    // reset the selected gain input(s) to default
    let currentStages = findCurrentGainStages(this.selectedPart);

    currentStages.forEach((gainStage) => {
      let gainSettingKey = camelCase(`${gainStage}GainSetting`),
        gainInputsKey = camelCase(`${gainStage}GainInputs`),
        gainSelectedInputKey = camelCase(`${gainStage}SelectedGainInput`);

      let defaultGainSetting = defaultItem && gainSettingKey in defaultItem ? defaultItem[gainSettingKey] : null,
        selectedGainInput = findSelectedInputByValue(this.selectedPart[gainInputsKey], defaultGainSetting);

      this.set(`selectedPart.${gainSelectedInputKey}`, selectedGainInput);
    });

    this.refreshGainForSelectedPart();
  }

  // Build URL Query Strings
  getURLHash() {
    let p = this.projectData,
      urlObject = {
        pr: this.selectedPart.id,
        ty: this.currentSignalType.id,
        l: p.varTypeLeft,
        r: p.varTypeRight,
        difL: p.vDiffLeft,
        difR: p.vDiffRight,
        sl: p.varTypeSlider,
        difSl: p.vDiffSlider,
        tab: this.selectedTab,
      };

    if (this.usesChargePumpSupply) urlObject.vddcp = p.chargePumpSupply;

    if (this.isThreeAmpClampedSchematic) {
      urlObject.vr2 = p.vref2;
      urlObject.vr1 = p.vref1;

      urlObject.vclhi = p.vclhi;
      urlObject.vcllo = p.vcllo;
    } else {
      urlObject.vr = p.vref;
    }

    if (this.isDiffPGIASchematic) {
      urlObject.vni = p.vsNegIn;
      urlObject.vpi = p.vsPosIn;
      urlObject.vpo = p.vsPosOut;

      if (this.isDiffTIAPGIASchematic) {
        urlObject.vno = p.vsNegOut;
      }
    } else {
      urlObject.vn = p.vsNeg;
      urlObject.vp = p.vsPos;
    }

    this.currentGainStages.forEach((stage) => {
      let urlKey = camelCase(`${stage}Gain`),
        selectedGainInputKey = camelCase(`${stage}SelectedGainInput`);
      urlObject[urlKey] = this.selectedPart[selectedGainInputKey].id;
    });

    return utilities.stringify(urlObject);
  }

  // Build Recommended Parts Table
  findRecommendedParts() {
    var pD = this.projectData,
      parts = this.parts,
      gainInput = this.selectedPart.selectedGainInput.id,
      suggestedParts = [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i],
        isDiffPGIAPart = this.isDiffPGIAPart(part),
        isThreeAmpClampedSchematic = this.isThreeAmpClampedSchematic,
        isDiffTIAPGIASchematic = this.isDiffTIAPGIASchematic,
        isDiffPGIASchematic = this.isDiffPGIASchematic,
        isDiff3AmpPGIASchematic = this.isDiff3AmpPGIASchematic,
        vref = this.vref,
        outputPosSupplies = isDiffPGIASchematic ? pD.vsPosOut : pD.vsPos,
        outputNegSupplies = isDiffPGIASchematic ? (isDiffTIAPGIASchematic ? pD.vsNegOut : pD.vsNegIn) : pD.vsNeg,
        inputPosSupplies = isDiffPGIASchematic ? pD.vsPosIn : pD.vsPos,
        inputNegSupplies = isDiffPGIASchematic ? pD.vsNegIn : pD.vsNeg,
        supplyRangeMin = isDiffPGIAPart ? part.inputSupplyRangeMin : part.supplyRangeMin,
        supplyRangeMax = isDiffPGIAPart ? part.inputSupplyRangeMax : part.supplyRangeMax,
        inVspText = part.inVspText || this.intl.t('inputMain.vsp'),
        inVsmText = part.inVsmText || this.intl.t('inputMain.vsn'),
        outVspText = part.outVspText || this.intl.t('inputMain.vsp'),
        outVsmText = part.outVsmText || this.intl.t('inputMain.vsn'),
        vref1Text = this.intl.t('inputMain.vref1'),
        vref2Text = this.intl.t('inputMain.vref2'),
        vclhiText = this.intl.t('inputMain.vclhi'),
        vclloText = this.intl.t('inputMain.vcllo'),
        vrefPosMargin = isDiffPGIAPart ? part.vocmPosMargin : part.inputHeadroomVsp,
        vrefNegMargin = isDiffPGIAPart ? part.vocmNegMargin : part.inputHeadroomVsm,
        supplyMarginInfo = {
          fieldId: [],
          validationType: 1,
          args: [inputNegSupplies, inputPosSupplies, supplyRangeMin, supplyRangeMax, inVspText, inVsmText],
        },
        vrefMarginInfo = {
          fieldId: [],
          validationType: 2,
          args: [outputNegSupplies, outputPosSupplies, vref, vrefPosMargin, vrefNegMargin],
        },
        gainRangeInfo = {
          fieldId: [],
          validationType: 3,
          args: [gainInput, part.gainRangeMin, part.gainRangeMax, part.gainType, part.gainInputs],
        },
        outputSupplyRangeInfo = {
          fieldId: [],
          validationType: 6,
          args: [
            outputNegSupplies,
            outputPosSupplies,
            part.outputSupplyRangeMin,
            part.outputSupplyRangeMax,
            outVspText,
            outVsmText,
          ],
        },
        outputNegSupplyInfo = {
          fieldId: [],
          validationType: 7,
          args: [outputNegSupplies, inputNegSupplies, inputPosSupplies, outVsmText, inVsmText, inVspText],
        },
        outputPosSupplyInfo = {
          fieldId: [],
          validationType: 8,
          args: [outputPosSupplies, inputPosSupplies, outVspText, inVspText],
        },
        vrefSpanInfo = {
          fieldId: [],
          validationType: 9,
          args: [pD.vref1, pD.vref2],
        },
        vref1MarginInfo = {
          fieldId: [],
          validationType: 2,
          args: [
            inputNegSupplies,
            inputPosSupplies,
            pD.vref1,
            vrefPosMargin,
            vrefNegMargin,
            vref1Text,
            inVspText,
            inVsmText,
          ],
        },
        vref2MarginInfo = {
          fieldId: [],
          validationType: 2,
          args: [
            inputNegSupplies,
            inputPosSupplies,
            pD.vref2,
            vrefPosMargin,
            vrefNegMargin,
            vref2Text,
            inVspText,
            inVsmText,
          ],
        },
        vclhiMarginInfo = {
          fieldId: [],
          validationType: 2,
          args: [
            inputNegSupplies,
            inputPosSupplies,
            pD.vclhi,
            vrefPosMargin,
            vrefNegMargin,
            vclhiText,
            inVspText,
            inVsmText,
          ],
        },
        vclloMarginInfo = {
          fieldId: [],
          validationType: 2,
          args: [
            inputNegSupplies,
            inputPosSupplies,
            pD.vcllo,
            vrefPosMargin,
            vrefNegMargin,
            vclloText,
            inVspText,
            inVsmText,
          ],
        };

      var validationFields = [supplyMarginInfo, gainRangeInfo],
        threeAmpValidation = [vrefMarginInfo],
        threeAmpClampedValidation = [vrefSpanInfo, vref1MarginInfo, vref2MarginInfo, vclhiMarginInfo, vclloMarginInfo],
        diffPGIAValidation = [outputSupplyRangeInfo],
        diffTIAPGIAValidation = [outputNegSupplyInfo],
        diff3AmpPGIAValidation = [outputPosSupplyInfo];

      if (isThreeAmpClampedSchematic) validationFields.push(...threeAmpClampedValidation);
      else validationFields.push(...threeAmpValidation);

      if (isDiffPGIASchematic) validationFields.push(...diffPGIAValidation);
      if (isDiffTIAPGIASchematic) validationFields.push(...diffTIAPGIAValidation);
      if (isDiff3AmpPGIASchematic) validationFields.push(...diff3AmpPGIAValidation);

      let results = validation.validate(validationFields, this.getPolygonsForPart(part), this.intl, part.id);
      var error = '';

      if (results[0] && results[1]) {
        if ('ideal3amp' != part.id.toLowerCase()) suggestedParts.push(part);
      } else {
        error = !results[4]
          ? this.intl.t('partSelector.supply')
          : !results[3]
            ? this.intl.t('partSelector.vref')
            : !results[2]
              ? this.intl.t('partSelector.gain')
              : !results[1]
                ? this.intl.t('partSelector.range')
                : '';
      }
      part.error = error;
    }

    suggestedParts = suggestedParts.sort(compare);

    this.setProperties({
      parts: parts,
      recommendedParts: suggestedParts,
      numOfRecommendedParts: suggestedParts.length,
    });
  }

  // Load using querystring values
  load() {
    var s = location.hash.split('?'),
      h = utilities.parse(s[0]),
      values = [],
      inconsistentInput = false,
      notNullCount = 0,
      urlQuestion;

    // see if there is a ?options coming in the correct spot (before the #)
    urlQuestion = document.location.search;
    // if there is no ?options before the #
    // check if by mistake there is one after the #
    if (0 === urlQuestion.length) {
      // We cache ? options for attaching to other URLS,
      // but we do not update our own URL putting ?options it the correct spot
      // as this would cause the page to reload.
      urlQuestion = s[1] != null && s[1].length !== 0 ? '?' + s[1] : '';
    }
    set(this, 'urlQuestion', urlQuestion);

    if (null != h.pr) {
      for (var i = 0; i < this.parts.length; i++) {
        if (this.parts[i].id.toLowerCase() == h.pr.toLowerCase()) {
          var lut = createLUT(this.parts);
          this.set('selectedPart', lut[this.parts[i].id]);
          this.urlSpecifiesDevice = true;
          break;
        }
      }
    }

    if (null != h.ty) {
      for (let i = 0; i < this.inputSignalTypes.length; i++) {
        if (this.inputSignalTypes[i].id == h.ty) {
          let lut = createLUT(this.inputSignalTypes);
          this.set('currentSignalType', lut[Number(h.ty)]);
          notNullCount++;
          break;
        }
      }
    }
    if (this.urlSpecifiesDevice) {
      if (this.isThreeAmpClampedPart(this.selectedPart)) {
        inconsistentInput = inconsistentInput || null != h.vr;
      } else {
        inconsistentInput = inconsistentInput || null != h.vr1 || null != h.vr2 || null != h.vclhi || null != h.vcllo;
      }

      if (this.isDiffPGIAPart(this.selectedPart)) {
        inconsistentInput = inconsistentInput || null != h.vn || null != h.vp;
        if (!this.isDiffTIAPGIAPart(this.selectedPart)) {
          inconsistentInput = inconsistentInput || null != h.vno;
        }
        if (!this.usesChargePumpSupply) {
          inconsistentInput = inconsistentInput || null != h.vddcp;
        }
      } else {
        inconsistentInput =
          inconsistentInput || null != h.vni || null != h.vpi || null != h.vpo || null != h.vno || null != h.vddcp;
      }
    }
    // If any query parameters are inconsistent with the part, keep the part number, but use defaults for that part for the rest.
    if (inconsistentInput) {
      this.setInputs();
    } else {
      if (null != h.vr) {
        values.push({ val: h.vr, setter: 'projectData.vref' });
        notNullCount++;
      }
      if (null != h.vr2) {
        values.push({ val: h.vr2, setter: 'projectData.vref2', undefinedSetter: 'projectData.vref' });
        notNullCount++;
      }
      if (null != h.vr1) {
        values.push({ val: h.vr1, setter: 'projectData.vref1', undefinedSetter: 'projectData.vref' });
        notNullCount++;
      }

      if (null != h.vn) {
        values.push({ val: h.vn, setter: 'projectData.vsNeg' });
        notNullCount++;
      }
      if (null != h.vni) {
        values.push({ val: h.vni, setter: 'projectData.vsNegIn', undefinedSetter: 'projectData.vsNeg' });
        notNullCount++;
      }
      if (null != h.vno) {
        values.push({ val: h.vno, setter: 'projectData.vsNegOut', undefinedSetter: 'projectData.vsNeg' });
        notNullCount++;
      }

      if (null != h.vp) {
        values.push({ val: h.vp, setter: 'projectData.vsPos' });
        notNullCount++;
      }
      if (null != h.vpi) {
        values.push({ val: h.vpi, setter: 'projectData.vsPosIn', undefinedSetter: 'projectData.vsPos' });
        notNullCount++;
      }
      if (null != h.vpo) {
        values.push({ val: h.vpo, setter: 'projectData.vsPosOut', undefinedSetter: 'projectData.vsPos' });
        notNullCount++;
      }

      if (null != h.vclhi) {
        values.push({ val: h.vclhi, setter: 'projectData.vclhi' });
        notNullCount++;
      }
      if (null != h.vcllo) {
        values.push({ val: h.vcllo, setter: 'projectData.vcllo' });
        notNullCount++;
      }

      if (null != h.vddcp) {
        values.push({ val: h.vddcp, setter: 'projectData.chargePumpSupply' });
        notNullCount++;
      }

      if (null != h.l) {
        values.push({ val: h.l, setter: 'projectData.varTypeLeft' });
        notNullCount++;
      }
      if (null != h.r) {
        values.push({ val: h.r, setter: 'projectData.varTypeRight' });
        notNullCount++;
      }
      if (null != h.difL) {
        values.push({ val: h.difL, setter: 'projectData.vDiffLeft' });
        notNullCount++;
      }
      if (null != h.difR) {
        values.push({ val: h.difR, setter: 'projectData.vDiffRight' });
        notNullCount++;
      }
      if (null != h.tab) {
        values.push({ val: h.tab, setter: 'selectedTab' });
        notNullCount++;
      }

      this.updateCalculatedProjectData();

      // Now weed out any parameters that are inconsistent with each other, giving preference to the parameters for PGIA and clamped parts

      for (let i = 0; i < values.length; i++) {
        if (isValidNumber(values[i].val)) {
          this.set(values[i].setter, Number(values[i].val));
          if (values[i].undefinedSetter) this.set(values[i].undefinedSetter, undefined);
        }
      }

      if (null != h.sl) {
        if (isValidNumber(h.sl))
          setSlider(
            Number(this.projectData.varTypeLeft),
            Number(this.projectData.varTypeRight),
            'projectData.varTypeSlider',
            Number(h.sl),
            this,
          );
        notNullCount++;
      } else {
        setSliderToMin(
          Number(this.projectData.varTypeLeft),
          Number(this.projectData.varTypeRight),
          'projectData.varTypeSlider',
          this,
        );
        this.set('projectData.varTypeSlider', this.projectData.varTypeLeft);
      }

      if (null != h.difSl) {
        if (isValidNumber(h.difSl))
          setSlider(
            Number(this.projectData.vDiffLeft),
            Number(this.projectData.vDiffRight),
            'projectData.vDiffSlider',
            Number(h.difSl),
            this,
          );
        notNullCount++;
      } else {
        setSliderToMin(
          Number(this.projectData.vDiffLeft),
          Number(this.projectData.vDiffRight),
          'projectData.vDiffSlider',
          this,
        );
        this.set('projectData.vDiffSlider', this.projectData.vDiffLeft);
      }
    }

    if (notNullCount !== 0) {
      if (!this.urlSpecifiesDevice) {
        this.findRecommendedParts();
        var part = this.recommendedParts[0];
        if (null != part) {
          this.set('selectedPart', part);
        }
      }
    }

    if (!inconsistentInput) {
      // Set the selected gain input from the url
      this.currentGainStages.forEach((stage) => {
        let urlKey = camelCase(`${stage}Gain`),
          gainTypeKey = camelCase(`${stage}GainType`),
          gainInputsKey = camelCase(`${stage}GainInputs`),
          selectedGainInputKey = camelCase(`${stage}SelectedGainInput`);

        if (urlKey in h && null != h[urlKey]) {
          if (this.selectedPart[gainTypeKey] != 'fixed' && isValidNumber(h[urlKey])) {
            if (this.selectedPart[gainTypeKey] == 'discrete') {
              for (let i = 0; i < this.selectedPart[gainInputsKey].length; i++) {
                if (this.selectedPart[gainInputsKey][i].id == h[urlKey]) {
                  notNullCount++;
                  this.set(`selectedPart.${selectedGainInputKey}`, getGainInput(Number(h[urlKey])));
                  break;
                }
              }
            } else {
              notNullCount++;
              this.set(`selectedPart.${selectedGainInputKey}`, getGainInput(Number(h[urlKey])));
            }
          }
        }
      });
    }

    this.refreshGainForSelectedPart();

    return this;
  }

  // Compute Polygon functions
  getInputSignalPolygon(p) {
    var o = this.projectData,
      numDiffLeft = Number(o.vDiffLeft),
      numDiffRight = Number(o.vDiffRight),
      numVarLeft = Number(o.varTypeLeft),
      numVarRight = Number(o.varTypeRight);

    return formulas.inputSignalPolygonQuad(
      {
        gain: Number(this.selectedPart.selectedGainInput.id),
        vref: Number(this.vref),
        vDiffMin: Math.min(numDiffLeft, numDiffRight),
        vDiffMax: Math.max(numDiffLeft, numDiffRight),
        vMin: Math.min(numVarLeft, numVarRight),
        vMax: Math.max(numVarLeft, numVarRight),
        isDiffPGIAPart: this.isDiffPGIAPart(p), // this is not necessarily the selected part - this poly gon is built for every part in the list, everytime
      },
      this.currentSignalType.id,
    );
  }

  getPreAmplifierInputPolygon(part) {
    var o = this.projectData,
      gain = {
        main: Number(this.selectedPart.selectedGainInput.id),
      };

    this.currentGainStages
      .filter((stage) => stage) // filter out the main stage
      .forEach((stage) => {
        let selectedGainInputKey = `${stage}SelectedGainInput`;
        if (selectedGainInputKey in this.selectedPart && this.selectedPart[selectedGainInputKey])
          gain[stage] = this.selectedPart[selectedGainInputKey].id;
      });

    return formulas.preAmplifierInputQuad({
      gain: gain,
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vref: Number(this.vref),
      preAmpInputPosMargin: Number(part.preAmpInputPosMargin),
      preAmpInputNegMargin: Number(part.preAmpInputNegMargin),
      isDiffPGIAPart: this.isDiffPGIAPart(part),
      isDiffTIAPGIAPart: this.isDiffTIAPGIAPart(part),
      isDiff3AmpPGIAPart: this.isDiff3AmpPGIAPart(part),
      // for dual supply PGIAs
      supplyPosIn: Number(o.vsPosIn),
      supplyNegIn: Number(o.vsNegIn),
      ro: Number(part.ro),
      rmirr: Number(part.rmirr),
    });
  }

  getPreAmplifierOutputQuad3Amp(part) {
    if (!this.isThreeAmpOrClampedPart(part) && !this.isDiff3AmpPGIAPart(part)) return [[]];

    var o = this.projectData;

    return formulas.preAmplifierOutputQuad3Amp({
      gda: part.gda,
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vref: Number(this.vref),
      preAmplifierOutputPosMargin: part.preAmpOutputPosMargin,
      preAmplifierOutputNegMargin: part.preAmpOutputNegMargin,
      vdir: part.vdir,
      // for diffPGIA
      isDiffPGIAPart: this.isDiffPGIAPart(part),
      isDiffTIAPGIAPart: this.isDiffTIAPGIAPart(part),
      isDiff3AmpPGIAPart: this.isDiff3AmpPGIAPart(part),
      supplyPosIn: Number(o.vsPosIn),
      supplyNegIn: Number(o.vsNegIn),
      gain: Number(this.selectedPart.selectedGainInput.id),
    });
  }

  getoutputAmplifierQuad2Amp(part) {
    if (part.inAmpType != '2amp') return [[]];
    var o = this.projectData;

    let go;
    if (part.diagramType == 'PINSTRAPPED') {
      // added to deal with pinstrapped 2amp parts - LTC
      go = Number(this.selectedPart.selectedGainInput.id);
    } else {
      go = part.gda;
    }
    return formulas.outputAmplifierQuad2Amp({
      gda: go,
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vref: Number(this.vref),
      vdir: part.vdir,
      outputAmpOutputPosMargin: part.outputAmpOutputPosMargin,
      outputAmpOutputNegMargin: part.outputAmpOutputNegMargin,
    });
  }

  getSubtractorInputOutput(part) {
    if (!this.isThreeAmpOrClampedPart(part) && !this.isDiffPGIAPart(part)) return [[]];
    var o = this.projectData;

    return formulas.subtractorInputOutput({
      gda: part.gda,
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vOHTop: Number(o.vsPos),
      vOLBottom: Number(o.vsNeg),
      vref: Number(this.vref),
      outputAmpOutputPosMargin: part.outputAmpOutputPosMargin,
      outputAmpOutputNegMargin: part.outputAmpOutputNegMargin,
      vSubtractorPos: part.vSubtractorPos,
      vSubtractorNeg: part.vSubtractorNeg,
      vdir: part.vdir,
      // for diffPGIA
      isDiffPGIAPart: this.isDiffPGIAPart(part),
      isDiffTIAPGIAPart: this.isDiffTIAPGIAPart(part),
      isDiff3AmpPGIAPart: this.isDiff3AmpPGIAPart(part),
      supplyPosOut: Number(o.vsPosOut),
      supplyNegOut: Number(o.vsNegOut),
      supplyPosIn: Number(o.vsPosIn),
      supplyNegIn: Number(o.vsNegIn),
      diffAmpPosMargin: part.vSubtractorPos,
      diffAmpNegMargin: part.vSubtractorNeg,
      gain: part.selectedGainInput.id,
      // for clamped outputs
      isThreeAmpClampedSelected: this.isThreeAmpClampedSchematic, // this is for currently selected part
      // this.isThreeAmpClampedPart(part) <- this is for part that's being validated
      // this.isThreeAmpClampedSchematic <- this is for the part that is selected
      vClampHi: Number(o.vclhi),
      vClampLo: Number(o.vcllo),
      clampHiMargin: part.clhimargin,
      clampLoMargin: part.cllomargin,
    });
  }

  getFeedBack1(part) {
    if (part.inAmpType != 'current') return [[]];
    var o = this.projectData;

    return formulas.feedBack1({
      gain: Number(this.selectedPart.selectedGainInput.id),
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vref: Number(this.vref),
      inputHeadroomVsp: part.inputHeadroomVsp,
      inputHeadroomVsm: part.inputHeadroomVsm,
      preAmpInputPosMargin: part.preAmpInputPosMargin,
      preAmpInputNegMargin: part.preAmpInputNegMargin,
    });
  }

  getFeedBack2(part) {
    if (part.inAmpType != 'current') return [[]];
    var o = this.projectData;

    return formulas.feedBack2({
      gain: Number(this.selectedPart.selectedGainInput.id),
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      vref: Number(this.vref),
      vdir: part.vdir,
      inputHeadroomVsp: part.inputHeadroomVsp,
      inputHeadroomVsm: part.inputHeadroomVsm,
      preAmpInputPosMargin: part.preAmpInputPosMargin,
      preAmpInputNegMargin: part.preAmpInputNegMargin,
      gda: part.gda,
    });
  }

  getOutputPinBoundry(part) {
    if (part.inAmpType != 'current') return [[]];

    var o = this.projectData;
    return formulas.outputPinBoundry({
      supplyPos: Number(o.vsPos),
      supplyNeg: Number(o.vsNeg),
      preAmpInputPosMargin: Number(part.preAmpInputPosMargin),
      preAmpInputNegMargin: Number(part.preAmpInputNegMargin),
      outputAmpOutputPosMargin: Number(part.outputAmpOutputPosMargin),
      outputAmpOutputNegMargin: Number(part.outputAmpOutputNegMargin),
    });
  }

  getPolygonsForPart(p) {
    return {
      inputSignal: this.getInputSignalPolygon(p),
      preAmplifierInput: this.getPreAmplifierInputPolygon(p),
      preAmplifierOutput3Amp: this.getPreAmplifierOutputQuad3Amp(p),
      subtractorInputOutput: this.getSubtractorInputOutput(p),
      outputAmplifier2Amp: this.getoutputAmplifierQuad2Amp(p),
      feedback1: this.getFeedBack1(p),
      feedback2: this.getFeedBack2(p),
      outputPinBoundry: this.getOutputPinBoundry(p),
    };
  }

  updateCurrentPolygons() {
    var pi = this.selectedPart,
      cp = this.currentPolygons,
      up = this.getPolygonsForPart(pi);

    cp.inputSignal = up.inputSignal;
    cp.preAmplifierInput = up.preAmplifierInput;
    cp.preAmplifierOutput3Amp = up.preAmplifierOutput3Amp;
    cp.subtractorInputOutput = up.subtractorInputOutput;
    cp.outputAmplifier2Amp = up.outputAmplifier2Amp;
    cp.feedback1 = up.feedback1;
    cp.feedback2 = up.feedback2;
    cp.outputPinBoundry = up.outputPinBoundry;

    this.set('currentPolygons', cp);
  }

  updateInnerPolygon() {
    let cp = this.currentPolygons,
      innerPolygon = intersection.intersect(cp);

    this.set('innerPolygon', innerPolygon || cp.inputSignal);
    this.set('innerPolygonError', innerPolygon ? false : true);
  }

  preparePlot() {
    if (!this.validationResults.isAllValid) {
      this.set('lines', [[[0, 0]]]);
      return;
    }

    var f = formulas.getPlotColorArrays(this.plotColors),
      s = formulas.getPlotStyleArrays(),
      p = this.currentPolygons,
      makeMarkBigger =
        this.projectData.vDiffLeft == this.projectData.vDiffRight &&
        this.projectData.varTypeLeft == this.projectData.varTypeRight
          ? true
          : false,
      plotData = this.isThreeAmpOrClampedSchematic
        ? formulas.prepare3AmpPartPlot(f, s, p, this.innerPolygon, makeMarkBigger)
        : this.selectedPart.inAmpType == '2amp'
          ? formulas.prepare2AmpPartPlot(f, s, p, this.innerPolygon, makeMarkBigger)
          : this.selectedPart.inAmpType == 'current'
            ? formulas.prepareCurrentPartPlot(f, s, p, this.innerPolygon, makeMarkBigger)
            : this.isDiffPGIASchematic
              ? formulas.prepareDiffPGIAPartPlot(f, s, p, this.innerPolygon, makeMarkBigger)
              : null;

    var concat = formulas.concatA;

    if (plotData) {
      this.set('polyColors', concat(plotData.colors));
      this.set('plotStyle', concat(plotData.styles));
      this.set('lines', concat(plotData.polygons));
    }
  }
}
